import { ArrayHelpers, useFormikContext } from 'formik';
import React, { CSSProperties, ReactElement, useState } from 'react';
import { BiAt, BiNote, BiPhone, BiUser } from 'react-icons/bi';
import { Client, Contact } from '../../../../../common/src/entities';
import ClientContact from '../../../../../common/src/entities/client-contact.entity';
import { useAlertDialog } from '../../../Utils/dialogContext';
import ChampMultiple, { affichageElementDefaut } from '../../Common/ChampMultiple';
import PopoverSimple from '../../Common/PopoverSimple';
import { CopierPressePapier } from '../Utils/CopierPressePapier';
import { PopoutEntite } from '../Utils/PopoutEntite';
import CreerContact from './CreerContact';
import { cssTexte } from '../constantes';

function AffichageTexte(props: { value: ClientContact }): ReactElement {
    let couleurCompte: string;
    let statusCompte: string;
    let isActif = props.value.contact.actif;
    const {
        value: { contact, poste },
    } = props;
    if (contact.password_hash && contact.updatePassword) {
        couleurCompte = 'gold';
        statusCompte = 'Mot de passe à changer';
    } else if (contact.password_hash && !contact.updatePassword) {
        couleurCompte = 'green';
        statusCompte = 'Compte créé';
    } else {
        couleurCompte = 'red';
        statusCompte = 'Pas de compte';
    }

    const compte = (
        <PopoverSimple texte={statusCompte}>
            <BiUser color={couleurCompte} />
        </PopoverSimple>
    );

    const telephone = (
        <CopierPressePapier texte={contact.telephone}>
            <PopoverSimple texte={contact.telephone} titre="Téléphone">
                <BiPhone />
            </PopoverSimple>
        </CopierPressePapier>
    );

    const email = (
        <CopierPressePapier texte={contact.email}>
            <PopoverSimple texte={contact.email} titre="Courriel">
                <BiAt />
            </PopoverSimple>
        </CopierPressePapier>
    );

    const notes = (
        <CopierPressePapier texte={contact.notes}>
            <PopoverSimple texte={contact.notes} titre="Notes">
                <BiNote />
            </PopoverSimple>
        </CopierPressePapier>
    );

    const nameTextStyle: CSSProperties = {
        color: isActif ? 'black' : 'grey',
    };

    return (
        <div style={cssTexte}>
            <span style={nameTextStyle}>{contact.name}</span>{' '}
            <span>
                {contact.telephone && telephone} {contact.email && email} {contact.notes && notes}{' '}
                {contact.password_hash && compte}
            </span>
        </div>
    );
}

interface PropsAffichageDetail {
    value: ClientContact;
}

function AffichageDetail(props: PropsAffichageDetail): ReactElement {
    const cssTexte: CSSProperties = {
        fontSize: '0.875rem',
    };

    return <div style={cssTexte}>{props.value.poste}</div>;
}

const affichageTexte = (value) => <AffichageTexte value={value} />;

export interface PropsChampMultipleClientContact {
    id: string;
    labelCol?: number;
    avecUnBouton?: boolean;
    details: (index: number) => string;
    suggestions: string[];
    placeholder?: string;
    clickCrayon?: (index: number) => void;
    disabled?: boolean;
}

export default function ChampMultipleClientContact(props: PropsChampMultipleClientContact): ReactElement {
    const affichageAjout = (values, helpers) => (
        <AffichageAjout helpers={helpers} values={values} avecUnBouton={props.avecUnBouton} />
    );
    const { values } = useFormikContext<ClientContact>();

    function isAllActif(values: ClientContact): boolean {
        for (const key in values['contacts']) {
            if (values['contacts'].hasOwnProperty(key)) {
                if (!values['contacts'][key]['actif']) {
                    return false;
                }
            }
        }
        return true;
    }
    interface PropsAffichageAjout {
        helpers: ArrayHelpers;
        values: ClientContact[];
        avecUnBouton?: boolean;
    }
    const [isToggled, setIsToggled] = useState(false);

    const handleToggle = () => {
        setIsToggled(!isToggled);
    };
    const allActif = isAllActif(values);
    const buttonStyle = !allActif ? {} : { color: 'grey' };
    const textButton = !isToggled ? 'Voir le(s) contact(s) inactif(s)' : 'Voir uniquement le(s) contact(s) actif(s)';
    function isContactActif(value: ClientContact | number, index: number): boolean {
        if (isToggled) {
            return true;
        } else {
            return value['contact']['actif'];
        }
    }
    function AffichageAjout(props: PropsAffichageAjout): ReactElement {
        const bsAlert = useAlertDialog();

        const { values: client } = useFormikContext<Client>();
        return (
            <div className="d-inline-flex">
                <PopoutEntite
                    titre="Ajouter un contact"
                    entite={Contact}
                    isCreatable
                    modalSize="lg"
                    modalBody={(props) => <CreerContact {...props} />}
                    size="sm"
                    onChange={(value) => {
                        const contact = value as Contact;
                        if (contact) {
                            if (props.values.find((item) => item.contact.id === contact.id)) {
                                bsAlert('Ce contact est déja associé!', 'warning');
                            } else {
                                //les fk sont requis pour l'enregistrement dans la bd
                                props.helpers.push({
                                    contact,
                                    fk_id_contact: contact.id,
                                    fk_id_client: client.id,
                                    debut: new Date().toISOString().split('T')[0],
                                    actif: true,
                                });
                            }
                        } else {
                            alert();
                        }
                    }}
                    avecUnBouton={props.avecUnBouton}
                />
                <button
                    className="btn btn-outline-primary ml-2 btn-sm"
                    hidden={allActif}
                    onClick={handleToggle}
                    style={buttonStyle}
                >
                    {textButton}
                </button>
            </div>
        );
    }
    return (
        <ChampMultiple
            entite={ClientContact}
            titre="Contacts"
            {...props}
            affichageAjout={!props.disabled ? affichageAjout : () => <></>}
            affichageElement={(value: ClientContact | number, index: number, helpers: ArrayHelpers) => {
                return affichageElementDefaut(Contact)(
                    (value as ClientContact).contact,
                    index,
                    helpers,
                    () => affichageTexte(value),
                    props.disabled,
                );
            }}
            clickCrayon={!props.disabled ? props.clickCrayon : () => {}}
            AffichageDetail={(value) => <AffichageDetail value={value} />}
            isActif={isContactActif}
        />
    );
}
